import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isHTMLSafe, htmlSafe } from '@ember/template'
import { find, differenceWith } from 'lodash'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { t, type IntlService } from 'ember-intl'
import type Student from 'teacher-ui-client/models/student'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { Legend, TD, TH, type LegendsItem } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import { SortColumnIcon } from 'teacher-ui-client/components/sort-column-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { formatValue } from 'teacher-ui-client/helpers/format-value'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import type { BooksReadByGenreHistoryData } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-genre-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  id: string
  studentId: string
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  booksRead: number
  fiction: number | null
  nonfiction: number | null
}

interface Args {
  booksReadByGenreData: BooksReadByGenreHistoryData[]
  students: Student[]
  subco: boolean
  uiScope: UIScopeName
  tooltip: string
  studentRouteName: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

type CategoryKey = 'fiction' | 'nonfiction'
const chartCategories: CategoryKey[] = ['nonfiction', 'fiction']
const legendCategories: CategoryKey[] = ['fiction', 'nonfiction']

interface ChartArgs {
  data: HistoryDataViewModel
  showYAxisLabels: boolean
  maxValue: number
}

const buildChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    opposite: true,
    max: chartOptions.maxValue,
    minorTicks: false,
    labels: {
      enabled: chartOptions.showYAxisLabels,
    },
    offset: -10,
  }

  const series = chartCategories.map((category) => {
    return {
      type: 'bar' as const,
      name: category,
      data: [chartOptions.data[category] ?? 0],
    }
  })

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}</b>`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [colors.purplyPink300, colors.purplyPink400],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class BooksReadByGenreHistory extends Component<Signature> {
  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  @service intl!: IntlService

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    const stats = this.args.booksReadByGenreData
    return this.args.students.filter(hasId).map((student) => {
      const statRow = find(stats, { student_id: parseInt(student.id, 10) })

      const fiction = statRow?.fiction ?? null
      const nonfiction = statRow?.nonfiction ?? null
      const booksRead = (fiction ?? 0) + (nonfiction ?? 0)

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        id: student.id,
        studentId: student.id,
        booksRead,
        fiction,
        nonfiction,
      }
    })
  }

  get amendedTooltip() {
    const { tooltip } = this.args
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.booksReadByGenreData ?? [],
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = this.args.tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get maxBooksRead() {
    const booksReadData = this.args.booksReadByGenreData.map((student) => student.nonfiction + student.fiction)

    const highestBooksRead = booksReadData ? Math.max(...booksReadData) : 10
    return Math.ceil(highestBooksRead / 5) * 5 + 5 // extra space on the chart (nearest multiple of 5)
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      return {
        ...row,
        chartOptions: buildChartOptions({
          data: row,
          showYAxisLabels: index === 0,
          maxValue: this.maxBooksRead,
        }),
      }
    })
  }

  get legendCategories(): LegendsItem[] {
    const { intl } = this

    return legendCategories.map((category) => {
      switch (category) {
        case 'fiction':
          return {
            color: 'bg-purply-pink-400',
            label: intl.t('reporting.charts.booksRead.genres.fiction'),
          }
        case 'nonfiction':
          return {
            color: 'bg-purply-pink-300',
            label: intl.t('reporting.charts.booksRead.genres.nonfiction'),
          }
        default:
          return { color: '', label: '' }
      }
    })
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo(this.args.studentRouteName, studentId)
  }

  <template>
    <div
      data-test-giraffe-books-read-by-genre-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-books-read-by-genre-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="flex overflow-x-auto print:block">
        <table data-test-history-table-books-read-by-genre class="w-full min-w-6xl overflow-hidden rounded-sm text-sm">
          <thead>
            <tr class="bg-purply-pink-300 divide-x divide-white font-semibold text-white">
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon class @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}

                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </TH>
              {{/if}}
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "fiction")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.booksRead.fictionBooksRead"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="fiction" />
                </div>
              </TH>
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "nonfiction")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.booksRead.nonFictionBooksRead"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="nonfiction" />
                </div>
              </TH>
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "booksRead")}}>
                <div class="flex items-center justify-between font-bold">
                  {{t "reporting.charts.booksRead.totalBooksRead"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="booksRead" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH
                  class="{{if @subco 'w-1/3' 'w-1/2'}}
                    border-none bg-white px-0! whitespace-nowrap text-black print:hidden"
                >
                  <Legend
                    data-test-books-read-by-genre-legend
                    @title={{t "reporting.chartTitles.booksReadByGenreHistory"}}
                    @categories={{this.legendCategories}}
                  />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each this.rowsWithChartOptions as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-by-genre-student-row={{index}}
                class="cursor-pointer"
                {{on "click" (fn this.transitionToStudentRoute row.id)}}
              >
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">
                      {{row.teacherName}}
                    </div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <TD class="text-center">
                  {{formatValue row.fiction}}
                </TD>
                <TD class="text-center">
                  {{formatValue row.nonfiction}}
                </TD>
                <TD data-test-by-genre-student-row-total class="text-center">
                  {{formatValue row.booksRead}}
                </TD>
                {{#if this.showChart}}
                  <TD class="border-dusty-black-100 cursor-default border-l px-0! py-0! print:hidden">
                    <BaseHighcharterComponent
                      data-test-books-read-by-genre-highchart-row
                      class="h-full"
                      @options={{row.chartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default BooksReadByGenreHistory
